import React from "react";
import { lookup } from "./i18n";
import { readStoredLang } from "./storedLang";

/**
 * ErrorBoundary global — empêche une page entièrement blanche en cas de crash
 * de rendu React. Affiche un message clair + bouton de rechargement.
 *
 * Ce composant est monté HORS du LanguageProvider (voir App.js, qui l'utilise pour
 * envelopper le Provider) : il ne peut donc pas utiliser useLanguage/useT. On lit
 * directement la préférence de langue persistée par LanguageContext (clé "eh_lang")
 * et on résout via lookup() (import relatif : ce fichier est lui-même mocké en
 * "@/lib/*" virtual dans certaines suites).
 */
function resolveErrorBoundaryStrings() {
  const lang = readStoredLang();
  return {
    title: lookup("common", "errorBoundary.title", lang),
    body: lookup("common", "errorBoundary.body", lang),
    reload: lookup("common", "errorBoundary.reload", lang),
  };
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log pour diagnostic (visible dans la console navigateur en production)
    console.error("[ErrorBoundary] Render crash:", error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const s = resolveErrorBoundaryStrings();
      return (
        <div
          data-testid="app-error-boundary"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#0f1419",
            color: "#fff",
            fontFamily: "system-ui, sans-serif",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: 440 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>☾</div>
            <h1 style={{ fontSize: 22, marginBottom: 12 }}>{s.title}</h1>
            <p style={{ opacity: 0.7, marginBottom: 24, lineHeight: 1.6 }}>
              {s.body}
            </p>
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                background: "#e9c46a",
                color: "#0f1419",
                border: "none",
                borderRadius: 999,
                padding: "12px 28px",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 15,
              }}
            >
              {s.reload}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
